import dayjs from 'dayjs';
import i18n from 'i18next';

const dateFormatPresets: Record<TPreset, Intl.DateTimeFormatOptions> = {
	weekDayShort: {
		weekday: 'short',
		year: '2-digit',
		month: '2-digit',
		day: '2-digit',
	},
	weekDayLong: {
		weekday: 'long',
		year: '2-digit',
		month: '2-digit',
		day: '2-digit',
	},
	dateNumeric: {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	},
	weekDayDateNumeric: {
		weekday: 'short',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	},
};

type TPreset =
	| 'weekDayShort'
	| 'weekDayLong'
	| 'dateNumeric'
	| 'weekDayDateNumeric';
type TIntlCfg = Intl.DateTimeFormatOptions;

export function formatDate(date: Date | string, cfg: TPreset): string;
export function formatDate(date: Date | string, cfg: TIntlCfg): string;
export function formatDate(date: Date | string): string;
export function formatDate(
	date: Date | string,
	cfg: TIntlCfg | TPreset = 'weekDayShort'
) {
	date = typeof date === 'string' ? new Date(date) : date;
	const locale = i18n.language.replace('_', '-');

	let preparedOptions: Intl.DateTimeFormatOptions;

	if (typeof cfg === 'string') {
		preparedOptions = dateFormatPresets[cfg];
	} else {
		preparedOptions = {
			...dateFormatPresets.weekDayShort,
			...cfg,
		};
	}

	return date.toLocaleDateString(locale, preparedOptions);
}

export const formatTime = (date: Date | string, options: TIntlCfg = {}) => {
	date = typeof date === 'string' ? new Date(date) : date;
	const locale = i18n.language.replace('_', '-');

	const preparedOptions: TIntlCfg = {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		...options,
	};

	return date.toLocaleTimeString(locale, preparedOptions);
};

export interface IDuration {
	months?: number;
	days?: number;
	hours?: number;
	minutes?: number;
	seconds?: number;
}

/**
 * Calculates duration between two dates and returns components (months, days, hours, minutes, seconds).
 * Uses dayjs to calculate the difference between start and end dates.
 *
 * @param start - Start date (Date, string, or dayjs-compatible input)
 * @param end - End date (Date, string, or dayjs-compatible input)
 * @returns IDuration object with time components
 *
 * @example
 * // Get full duration breakdown
 * const duration = calculateDuration(startDate, endDate);
 * console.log(duration.hours, duration.minutes);
 *
 * @example
 * // Get just minutes
 * const { minutes } = calculateDuration(startDate, endDate);
 */
export const calculateDuration = (
	start: Date | string,
	end: Date | string
): IDuration => {
	const startDate = dayjs(start);
	const endDate = dayjs(end);

	const months = endDate.diff(startDate, 'month');
	const afterMonths = startDate.add(months, 'month');
	const days = endDate.diff(afterMonths, 'day');
	const afterDays = afterMonths.add(days, 'day');
	const hours = endDate.diff(afterDays, 'hour');
	const afterHours = afterDays.add(hours, 'hour');
	const minutes = endDate.diff(afterHours, 'minute');
	const afterMinutes = afterHours.add(minutes, 'minute');
	const seconds = endDate.diff(afterMinutes, 'second');

	return {
		months,
		days,
		hours,
		minutes,
		seconds,
	};
};

/**
 * Calculates duration in minutes between two dates.
 * Convenience function for simple minute-based durations.
 *
 * @param start - Start date (Date, string, or dayjs-compatible input)
 * @param end - End date (Date, string, or dayjs-compatible input)
 * @returns Total duration in minutes
 *
 * @example
 * const durationMinutes = calculateDurationInMinutes(leg.start, leg.end);
 */
export const calculateDurationInMinutes = (
	start: Date | string,
	end: Date | string
): number => {
	return dayjs(end).diff(dayjs(start), 'minute');
};
